import { SymbolArtwork } from './SymbolResources';
import { isImageGraphic } from '../../../symbols/model';
import { pointPresentation, markerPresentation } from '../../../symbols/scene';
import {
  legendAreaOutline,
  legendLineDashes,
  simpleLegendSample,
  markerLegendLongEdge,
  type LegendSample,
} from '../../../composition/legendSamples';
import type { GeometryFamily } from '../../../styles/geometry';
import type { PointSymbol, VectorSymbols } from '../../../styles/model';
import type { AnnotationLegendSymbol } from '../../../annotations/legend';

interface SymbolPreviewProps {
  readonly sample?: LegendSample;
  readonly geometry: GeometryFamily | 'mixed';
  readonly label?: string;
  readonly symbols: VectorSymbols | readonly VectorSymbols[];
}

function pointShape(symbol: PointSymbol, x: number, size: number, sample: LegendSample) {
  const y = sample.height / 2;
  if (isImageGraphic(symbol.shape))
    return (
      <SymbolArtwork
        graphic={symbol.shape}
        colour={symbol.fillColor}
        {...(symbol.tint === undefined ? {} : { tint: symbol.tint })}
        x={x}
        y={y}
        longEdge={size * 2}
        rotation={symbol.rotationDegrees ?? 0}
        opacity={symbol.opacity}
        presentation={pointPresentation(symbol)}
      />
    );
  const common = {
    fill: symbol.fillColor,
    fillOpacity: symbol.opacity,
    stroke: symbol.outlineColor,
    strokeOpacity: symbol.opacity,
    strokeWidth: sample.pointOutline,
  };
  if (symbol.shape === 'square') {
    return <rect {...common} height={size * 2} width={size * 2} x={x - size} y={y - size} />;
  }
  if (symbol.shape === 'triangle') {
    return (
      <polygon
        {...common}
        points={`${x},${y - size * 1.15} ${x - size},${y + size * 0.85} ${x + size},${y + size * 0.85}`}
      />
    );
  }
  return <circle {...common} cx={x} cy={y} r={size} />;
}

function familyForMixed(index: number): GeometryFamily {
  return (['polygon', 'line', 'point'] as const)[index % 3] ?? 'point';
}

export function SymbolPreview({ geometry, label, symbols, sample }: SymbolPreviewProps) {
  const entries = (Array.isArray(symbols) ? symbols : [symbols]).slice(0, 4) as VectorSymbols[];
  const slotWidth = 30 / entries.length;
  const height =
    sample?.height ?? Math.max(18, ...entries.map((e) => simpleLegendSample(e).height));
  return (
    <svg
      aria-hidden={label ? undefined : true}
      aria-label={label}
      className="symbol-preview"
      data-symbol-geometry={geometry}
      height={height}
      role={label ? 'img' : undefined}
      viewBox={`0 0 30 ${height}`}
      width="30"
    >
      {entries.map((entry, index) => {
        const family = geometry === 'mixed' ? familyForMixed(index) : geometry;
        const sizing = { ...(sample ?? simpleLegendSample(entry)), height };
        const x = slotWidth * index + slotWidth / 2;
        if (family === 'point') {
          const size = Math.min(sizing.pointRadius, (slotWidth - 1) / 2);
          return <g key={index}>{pointShape(entry.point, x, size, sizing)}</g>;
        }
        if (family === 'line') {
          const width = sizing.lineWidth;
          return (
            <g key={index}>
              <line
                opacity={entry.line.opacity}
                stroke={entry.line.color}
                strokeDasharray={legendLineDashes(entry.line.dash, width).join(' ') || undefined}
                strokeLinecap="round"
                strokeWidth={width}
                x1={slotWidth * index + 1}
                x2={slotWidth * (index + 1) - 1}
                y1={height / 2}
                y2={height / 2}
              />
            </g>
          );
        }
        return (
          <rect
            fill={entry.polygon.fillColor}
            fillOpacity={entry.polygon.fillOpacity}
            height="12"
            key={index}
            stroke={entry.polygon.outlineColor}
            strokeWidth={legendAreaOutline(entry.polygon.outlineWidth)}
            width={Math.max(3, slotWidth - 3)}
            x={slotWidth * index + 1.5}
            y={height / 2 - 6}
          />
        );
      })}
    </svg>
  );
}

export function AnnotationLegendSymbolPreview({
  label,
  symbol,
}: {
  readonly label?: string;
  readonly symbol: AnnotationLegendSymbol;
}) {
  const accessibility = {
    'aria-hidden': label ? undefined : (true as const),
    'aria-label': label,
    role: label ? ('img' as const) : undefined,
  };
  if (symbol.kind === 'marker') {
    const size = markerLegendLongEdge(symbol) / 2;
    const height = isImageGraphic(symbol.shape) && symbol.backing?.enabled ? 30 : 18;
    const common = {
      fill: symbol.fillColour,
      fillOpacity: symbol.opacity,
      stroke: symbol.outlineColour,
      strokeOpacity: symbol.opacity,
      strokeWidth: 1.5,
    };
    return (
      <svg
        {...accessibility}
        className="symbol-preview"
        data-annotation-symbol-kind="marker"
        height={height}
        viewBox={`0 0 30 ${height}`}
        width="30"
      >
        {isImageGraphic(symbol.shape) ? (
          <SymbolArtwork
            graphic={symbol.shape}
            colour={symbol.fillColour}
            {...(symbol.tint === undefined ? {} : { tint: symbol.tint })}
            longEdge={size * 2}
            y={height / 2}
            rotation={symbol.rotationDegrees ?? 0}
            opacity={symbol.opacity}
            presentation={markerPresentation(symbol)}
          />
        ) : symbol.shape === 'circle' ? (
          <circle {...common} cx="15" cy="9" r={size} />
        ) : symbol.shape === 'diamond' ? (
          <polygon
            {...common}
            points={`15,${9 - size} ${15 + size},9 15,${9 + size} ${15 - size},9`}
          />
        ) : (
          <rect {...common} height={size * 2} width={size * 2} x={15 - size} y={9 - size} />
        )}
      </svg>
    );
  }
  if (symbol.kind === 'line') {
    const width = Math.min(6, Math.max(1.4, symbol.width * 0.72));
    const endpoint =
      symbol.endStyle === 'filled-circle' ? (
        <circle cx="26" cy="9" fill={symbol.colour} r={Math.max(2.2, width)} />
      ) : symbol.endStyle === 'filled-square' ? (
        <rect
          fill={symbol.colour}
          height={Math.max(4.4, width * 2)}
          width={Math.max(4.4, width * 2)}
          x={26 - Math.max(2.2, width)}
          y={9 - Math.max(2.2, width)}
        />
      ) : symbol.endStyle === 'arrow' ? (
        <polygon fill={symbol.colour} points="28,9 22,5 22,13" />
      ) : null;
    return (
      <svg
        {...accessibility}
        className="symbol-preview"
        data-annotation-symbol-kind="line"
        height="18"
        viewBox="0 0 30 18"
        width="30"
      >
        <line
          opacity={symbol.opacity}
          stroke={symbol.colour}
          strokeDasharray={symbol.dash === 'dashed' ? '7 4' : undefined}
          strokeLinecap="round"
          strokeWidth={width}
          x1="2"
          x2="27"
          y1="9"
          y2="9"
        />
        <g opacity={symbol.opacity}>{endpoint}</g>
      </svg>
    );
  }
  if (symbol.kind === 'area') {
    const common = {
      fill: symbol.fillColour,
      fillOpacity: symbol.fillOpacity,
      stroke: symbol.outlineColour,
      strokeDasharray: symbol.outlineDash === 'dashed' ? '5 3' : undefined,
      strokeWidth: legendAreaOutline(symbol.outlineWidth),
    };
    return (
      <svg
        {...accessibility}
        className="symbol-preview"
        data-annotation-symbol-kind="area"
        height="18"
        viewBox="0 0 30 18"
        width="30"
      >
        {symbol.shape === 'ellipse' ? (
          <ellipse {...common} cx="15" cy="9" rx="13" ry="6" />
        ) : (
          <rect
            {...common}
            height="12"
            rx={symbol.shape === 'rectangle' ? Math.min(5, symbol.cornerRadius / 4) : 0}
            width="27"
            x="1.5"
            y="3"
          />
        )}
      </svg>
    );
  }
  const fadeOpacity = symbol.fadeOpacity;
  const boundary = symbol.boundaryVisible ? (
    <rect
      fill="none"
      height="8"
      stroke={symbol.boundaryColour}
      strokeDasharray={symbol.boundaryDash === 'dashed' ? '4 2' : undefined}
      strokeWidth={Math.min(2.5, Math.max(0.75, symbol.boundaryWidth * 0.6))}
      width="17"
      x="6.5"
      y="5"
    />
  ) : null;
  return (
    <svg
      {...accessibility}
      className="symbol-preview"
      data-annotation-symbol-kind="focus-area"
      height="18"
      viewBox="0 0 30 18"
      width="30"
    >
      {symbol.fadeDirection === 'inside' ? (
        <rect
          fill={symbol.fadeColour}
          fillOpacity={fadeOpacity}
          height="8"
          width="17"
          x="6.5"
          y="5"
        />
      ) : (
        <>
          <rect
            fill={symbol.fadeColour}
            fillOpacity={fadeOpacity}
            height="4"
            width="28"
            x="1"
            y="1"
          />
          <rect
            fill={symbol.fadeColour}
            fillOpacity={fadeOpacity}
            height="4"
            width="28"
            x="1"
            y="13"
          />
          <rect
            fill={symbol.fadeColour}
            fillOpacity={fadeOpacity}
            height="8"
            width="5.5"
            x="1"
            y="5"
          />
          <rect
            fill={symbol.fadeColour}
            fillOpacity={fadeOpacity}
            height="8"
            width="5.5"
            x="23.5"
            y="5"
          />
        </>
      )}
      {boundary}
    </svg>
  );
}
